import {CommonModule} from '@angular/common';
import {HttpClientModule} from '@angular/common/http';
import {APP_INITIALIZER, ErrorHandler, NgModule} from '@angular/core';
import {BrowserModule, Title} from '@angular/platform-browser';
import {UrlSerializer} from '@angular/router';
import {AppRoutingModule} from './app-routing.module';
import {AppContainer} from './app.container';

import {MAT_MOMENT_DATE_ADAPTER_OPTIONS, MatMomentDateModule} from '@angular/material-moment-adapter';
import {MAT_DATE_FORMATS} from '@angular/material/core';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {TippyModule, popperVariation, tooltipVariation} from '@ngneat/helipopper';
import {StoreRouterConnectingModule} from '@ngrx/router-store';
import {Store, StoreModule} from '@ngrx/store';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';
import {HeaderModule} from '@shared/components/header/header.module';
import {LeftPanelModule} from '@shared/components/left-panel/left-panel.module';
import {MDM_DATE_FORMATS} from '@shared/constants/formats.constants';
import {DirectivesModule} from '@shared/directives/directives.module';
import {CleanUrlSerializer} from '@shared/helpers/clean-url-serializer.helper';
import {INITIAL_LOADER_STATES, InitialLoaderHelper} from '@shared/helpers/initial-loader.helper';
import {projectPermissions} from '@shared/types/permissions.types';
import {AppState, reducers} from '@store';
import {StoreEffectsModule} from '@store/effects.module';
import {
	API_CONFIG,
	AmcIconifyModule,
	AmcPermissionsCurrentUserStoreFeatureModule,
	AmcProjectPermissionsStoreFeatureModule,
	ApiService,
	AppInitService,
	AuthDataStorageService,
	AuthGuard,
	ErrorInterceptorService,
	PermissionsGuard,
	PermissionsLoaderService,
	ProjectPermissionsActions,
	UserPermissionDirectiveModule,
	UserPermissionsHelper,
	UserRolesHelper,
	UserService,
} from 'amc-common-components';
import {CookieService} from 'ngx-cookie-service';
import {AppComponent} from './app.component';
import {ReactiveFormsModule} from '@angular/forms';
import {CtsDistributionGuard} from '@shared/services/cts-distribution.guard';

/**
 * App Loader
 * @param {AppInitService} appInitService
 * @returns {void}
 */
export function appLoader(appInitService: AppInitService) {
	return (): Promise<void> => {
		InitialLoaderHelper.setText(INITIAL_LOADER_STATES.LOADING_CONFIGS);
		return appInitService.initialize();
	};
}

/**
 * Permissions Initializer
 * @param {Store<AppState>} store
 * @param {AppInitService} appInitService
 * @param {PermissionsLoaderService} permissionsInitServe
 * @returns {void}
 */
export function permissionsInitializer(
	store: Store<AppState>,
	appInitService: AppInitService,
	permissionsInitServe: PermissionsLoaderService
) {
	return (): Promise<void> =>
		appInitService
			.initialize()
			.then(() => permissionsInitServe.initialize())
			.then(() => {
				UserRolesHelper.initialize();
				UserPermissionsHelper.setProjectPermissions(projectPermissions);
				store.dispatch(
					new ProjectPermissionsActions.SetPermissions({
						permissions: projectPermissions,
					})
				);
				console.warn('USER_ROLES >>  ', Object.keys(UserPermissionsHelper.USER_PERMISSIONS));
			});
}

@NgModule({
	declarations: [AppComponent, AppContainer],
	imports: [
		AppRoutingModule,
		CommonModule,
		BrowserModule,
		HttpClientModule,
		BrowserAnimationsModule,
		StoreRouterConnectingModule.forRoot({stateKey: 'router'}),
		StoreModule.forRoot(reducers),
		StoreEffectsModule,
		StoreDevtoolsModule.instrument(),
		DirectivesModule,
		AmcIconifyModule,
		ReactiveFormsModule,
		LeftPanelModule,
		HeaderModule,
		MatMomentDateModule,
		UserPermissionDirectiveModule,
		AmcPermissionsCurrentUserStoreFeatureModule,
		AmcProjectPermissionsStoreFeatureModule,
		TippyModule.forRoot({
			defaultVariation: 'popper',
			variations: {
				tooltip: tooltipVariation,
				popper: popperVariation,
			},
		}),
	],
	providers: [
		AuthGuard,
		CtsDistributionGuard,
		AppInitService,
		ApiService,
		UserService,
		CookieService,
		AuthDataStorageService,
		PermissionsLoaderService,
		PermissionsGuard,
		Title,
		{provide: APP_INITIALIZER, useFactory: appLoader, deps: [AppInitService], multi: true},
		{
			provide: APP_INITIALIZER,
			useFactory: permissionsInitializer,
			deps: [Store, AppInitService, PermissionsLoaderService],
			multi: true,
		},
		{provide: API_CONFIG, useValue: ApiService.NoCache},
		{provide: ErrorHandler, useClass: ErrorInterceptorService},
		{provide: UrlSerializer, useClass: CleanUrlSerializer},
		{provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: {useUtc: false}},
		{provide: MAT_DATE_FORMATS, useValue: MDM_DATE_FORMATS},
	],
	bootstrap: [AppContainer],
})
export class AppModule {}
